// AI Video Studio (SPRINT-105.8) — client-side scene transforms + constants.
// Scene reorder/duplicate/split/merge/enable are pure data transforms saved via
// autosave (PUT). The canonical Scene shape mirrors the backend video_studio module.
import { ASPECT_RATIOS } from "./creativeUtils";

export const TRANSITIONS = ["cut", "fade", "dissolve", "slide", "wipe"];
export const SCENE_KINDS = [{ value: "image", label: "Image" }, { value: "video", label: "Video" }];
export { ASPECT_RATIOS };

export const SCENE_STATUS = {
  not_generated: { label: "Not Generated", tone: "bg-muted text-muted-foreground" },
  queued: { label: "Queued", tone: "bg-muted text-muted-foreground" },
  generating: { label: "Generating", tone: "bg-warning/15 text-warning" },
  generated: { label: "Generated", tone: "bg-success/15 text-success" },
  failed: { label: "Failed", tone: "bg-destructive/15 text-destructive" },
};

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`);

export function newScene(partial = {}) {
  return {
    id: uid(), title: partial.title || `Scene ${partial.n || ""}`.trim(), kind: partial.kind || "image",
    structured: partial.structured || {}, duration_seconds: partial.duration_seconds ?? 5,
    aspect_ratio: partial.aspect_ratio || "16:9", transition: partial.transition || "cut",
    enabled: partial.enabled ?? true, notes: partial.notes || "", status: "not_generated",
    asset_ids: [], last_job_id: null, error: null, extensions: {},
  };
}

export const scenesOf = (p) => (p?.storyboard?.scenes || []);

export function withScenes(project, scenes) {
  return { ...project, storyboard: { ...(project.storyboard || {}), scenes } };
}

export function moveScene(scenes, index, dir) {
  const to = index + dir;
  if (to < 0 || to >= scenes.length) return scenes;
  const next = [...scenes];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

export function duplicateScene(scenes, index) {
  const s = scenes[index];
  const copy = { ...newScene(), ...s, id: newScene().id, title: `${s.title} (copy)`, status: "not_generated", asset_ids: [], last_job_id: null, error: null };
  const next = [...scenes];
  next.splice(index + 1, 0, copy);
  return next;
}

export function splitScene(scenes, index) {
  const s = scenes[index];
  const half = Math.max(1, Math.round((s.duration_seconds || 2) / 2));
  const a = { ...s, duration_seconds: half, title: `${s.title} A` };
  const b = { ...newScene(), ...s, id: newScene().id, duration_seconds: Math.max(1, (s.duration_seconds || 2) - half), title: `${s.title} B`, status: "not_generated", asset_ids: [], last_job_id: null, error: null };
  const next = [...scenes];
  next.splice(index, 1, a, b);
  return next;
}

export function mergeScene(scenes, index) {
  if (index >= scenes.length - 1) return scenes;
  const a = scenes[index], b = scenes[index + 1];
  const merged = {
    ...a, title: `${a.title} + ${b.title}`,
    duration_seconds: (a.duration_seconds || 0) + (b.duration_seconds || 0),
    notes: [a.notes, b.notes].filter(Boolean).join(" | "),
    structured: { ...a.structured, subject: [a.structured?.subject, b.structured?.subject].filter(Boolean).join("; ") },
    status: "not_generated", asset_ids: [], last_job_id: null, error: null,
  };
  const next = [...scenes];
  next.splice(index, 2, merged);
  return next;
}

export function fmtDuration(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}
